import React from 'react';

import { createRoot } from 'react-dom/client';

import { $config } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { i18n } from '@lingui/core';
import {
  AdServer,
  ContainerId,
  type LayoutType,
  type PlacementId,
  PlacementStatus,
  debugLog
} from '@schibsted-nmp/advertising-shared';

import { SponsoredProductAd } from './ads/SponsoredProductAd.js';
import {
  DsaBrand,
  type RetailMediaPayload,
  type RetailMediaProduct
} from './types/index.js';
import {
  callApi,
  RateLimitError,
  getVisitorId
} from './utils/RetailMediaApi.js';

const request = async (
  container: PlacementId,
  searchQuery: string,
  categories: string[] | undefined,
  initialLayoutType?: LayoutType,
  brandNames?: Record<string, string>
) => {
  updatePlacementKeyValueById(container, 'status', 'requested');
  updateMetricByKey(container, PlacementStatus.AdLoaded);

  const brand = $config.get()?.brand;
  const dsaBrand = DsaBrand.is(brand) ? brand : undefined;

  try {
    const payload: RetailMediaPayload = {
      zone: 'recommerce_search', // Currently hardcoded - will support multiple zones (e.g., recommerce_object) later
      visitorId: getVisitorId(),
      sponsoredProductsCount: 1,
      searchTerm: searchQuery,
      ...(categories && categories.length > 0 && { categories }),
      ...(brandNames &&
        Object.keys(brandNames).length > 0 && {
          brandNames: Object.values(brandNames)
        }),
      debug: true,
      userAgent: navigator.userAgent,
      ...(dsaBrand && { dsaBrand })
    };

    const data = await callApi(payload);
    debugLog('retail_media -> API response:', data);

    updatePlacementKeyValueById(container, 'creativeType', 'native-retail');
    updatePlacementKeyValueById(container, 'status', 'loaded');

    renderSponsoredListingAd(
      container,
      data.sponsoredProducts,
      initialLayoutType,
      categories
    );
  } catch (error) {
    // Ignore rate-limited requests - the first request will handle rendering
    if (error instanceof RateLimitError) {
      debugLog('retail_media -> Rate-limited request ignored:', error.message);
      return;
    }
    debugLog('retail_media -> Error in request:', error);
    updateMetricByKey(container, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(container, 'status', 'error');
  }
};

export function triggerRequest(
  container: PlacementId,
  searchQuery: string | string[] | undefined,
  categories: string[] | undefined,
  initialLayoutType?: LayoutType,
  brandNames?: Record<string, string>
) {
  const normalizedQuery = Array.isArray(searchQuery)
    ? searchQuery[0]
    : searchQuery;

  void request(
    container,
    normalizedQuery || '',
    categories,
    initialLayoutType,
    brandNames
  );
}

function renderSponsoredListingAd(
  placementId: PlacementId,
  products?: Array<RetailMediaProduct>,
  initialLayoutType?: LayoutType,
  categories?: string[]
) {
  setTimeout(() => {
    const containerId = ContainerId.fromPlacementId(
      placementId,
      AdServer.Retailmedia
    );
    const slotElement = document.getElementById(containerId);
    if (!slotElement) {
      debugLog('retail_media -> Slot element not found:', containerId);
      return;
    }

    // Clear the slot
    slotElement.innerHTML = '';
    slotElement.style.gridColumn = '1 / 1';

    // Handle no-fill case
    if (!products || products.length === 0) {
      debugLog('retail_media -> No-fill case for placement:', placementId);
      updateMetricByKey(placementId, PlacementStatus.AdNoFill);
      updatePlacementKeyValueById(placementId, 'status', 'error');
      return;
    }

    const originalLocale = i18n.locale;
    const supportedLocale = ['fi', 'nb', 'da', 'sv'].includes(i18n.locale)
      ? i18n.locale
      : 'nb'; // Default to Norwegian

    if (originalLocale !== supportedLocale) {
      i18n.activate(supportedLocale);
    }

    // Create a container for the React component
    const container = document.createElement('div');
    slotElement.appendChild(container);

    const layout = initialLayoutType ?? ('grid' as LayoutType);

    // Render the React component into the container
    const root = createRoot(container);
    root.render(
      <SponsoredProductAd
        layout={layout}
        products={products}
        placementId={placementId}
        categories={categories}
      />
    );

    if (originalLocale !== supportedLocale) {
      setTimeout(() => {
        i18n.activate(originalLocale);
      }, 0);
    }
  }, 1);
}
